import React, { useEffect, useRef } from 'react';
import { cn } from '@/utils/cn';
import { Button } from '@/components/common/Button';
import { useNavigate } from 'react-router-dom';

interface IntegrationsProps extends React.HTMLAttributes<HTMLDivElement> {
  tag?: string;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  apps?: Array<{
    id: string;
    name: string;
    icon: string;
  }>;
  animationEnabled?: boolean;
}

const HSIntegrations = ({
  className,
  tag = "INTEGRATIONS",
  title = "1,000+ pre-built connectors to deliver native experiences",
  subtitle = "Instantly connect popular apps with our growing library of pre-built connectors",
  buttonText = "Browse Integration",
  apps = [],
  animationEnabled = true,
  ...props
}: IntegrationsProps) => {
  // Sample apps if none provided
  const navigate = useNavigate();

  const defaultApps = [
    { id: '1', name: 'App1', icon: '/icons/app1.png' },
    { id: '2', name: 'App2', icon: '/icons/app2.png' },
    { id: '3', name: 'App3', icon: '/icons/app3.png' },
    { id: '4', name: 'App4', icon: '/icons/app4.png' },
    { id: '5', name: 'App5', icon: '/icons/app5.png' },
    { id: '6', name: 'App6', icon: '/icons/app6.png' },
    { id: '7', name: 'App7', icon: '/icons/app7.png' },
    { id: '8', name: 'App8', icon: '/icons/app8.png' },
    { id: '9', name: 'App9', icon: '/icons/app9.png' },
    { id: '10', name: 'App10', icon: '/icons/app10.png' }
  ];

  const appsToRender = apps.length > 0 ? apps : defaultApps;
  const firstRowRef = useRef<HTMLDivElement>(null);
  const secondRowRef = useRef<HTMLDivElement>(null);

  // Animation for app rows
  useEffect(() => {
    const firstRowContainer = firstRowRef.current;
    const secondRowContainer = secondRowRef.current;

    if (!firstRowContainer || !secondRowContainer) return;

    // Calculate the full width of the content (one set of app icons)
    const calculateContentWidth = (container: HTMLDivElement): number => {
      // Using first child's width and the space between items
      const firstChild = container.firstElementChild as HTMLElement;
      if (!firstChild) return 0;

      const itemWidth = firstChild.offsetWidth;
      const gap = 16; // space-x-4 = 16px
      return (itemWidth + gap) * appsToRender.length;
    };

    const firstRowWidth = calculateContentWidth(firstRowContainer);
    const secondRowWidth = calculateContentWidth(secondRowContainer);

    // Set CSS variables for animation
    document.documentElement.style.setProperty('--marquee-first-width', `${firstRowWidth}px`);
    document.documentElement.style.setProperty('--marquee-second-width', `${secondRowWidth}px`);

    // No need to add/remove classes as we're handling that in the JSX directly
  }, [appsToRender.length]);

  return (
    <section className={cn("py-16 md:py-24 px-4 md:px-6 bg-black text-white overflow-hidden", className)} {...props}>
      <div className="container mx-auto max-w-7xl">
        <div className="md:flex md:justify-between md:items-center">
          <div className="max-w-xl md:max-w-2xl">
            {/* Tag */}
            <div className="flex mb-6">
              <div className="inline-flex items-center justify-center px-4 py-2 rounded-full border border-blue-500 text-blue-400 font-medium text-sm">
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {tag}
              </div>
            </div>

            {/* Title and Subtitle */}
            <div className="text-left mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold mb-6">
                1,000+ pre-built connectors <br className="hidden md:block" />
                to deliver native <br className="hidden md:block" />
                experiences
              </h2>
              <p className="text-gray-400 text-lg">
                {subtitle}
              </p>
            </div>
          </div>

          {/* Button - Desktop Position */}
          <div className="hidden md:block">
            <Button
              variant="solid"
              color="primary"
              size="lg"
              onClick={() => {
                navigate("/integrations");
                window.scrollTo(0, 0);
              }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
            >
              {buttonText}
            </Button>
          </div>
        </div>

        {/* App Icons Container */}
        <div className="mt-12 md:mt-16">
          <style>
            {`
            @keyframes marqueeLeft {
              0% { transform: translateX(0); }
              100% { transform: translateX(calc(-1 * var(--marquee-first-width))); }
            }

            @keyframes marqueeRight {
              0% { transform: translateX(calc(-1 * var(--marquee-second-width))); }
              100% { transform: translateX(0); }
            }

            .marquee-first {
              animation: marqueeLeft 120s linear infinite;
              animation-play-state: running;
            }

            .marquee-second {
              animation: marqueeRight 120s linear infinite;
              animation-play-state: running;
            }

            .marquee-container {
              position: relative;
              mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
              -webkit-mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
            }

            /* Pause animations */
            .marquee-first.paused, .marquee-second.paused {
              animation-play-state: paused;
            }

            /* Pause animations when user prefers reduced motion */
            @media (prefers-reduced-motion) {
              .marquee-first, .marquee-second {
                animation-play-state: paused;
              }
            }
            `}
          </style>

          {/* App Icons - Top Row with left to right animation */}
          <div className="overflow-hidden mb-6 md:mb-16 marquee-container">
            <div
              ref={firstRowRef}
              className="flex space-x-4 py-2 marquee-first"
              style={{ width: 'fit-content' }}
            >
              {/* Double the items for seamless looping */}
              {[...appsToRender, ...appsToRender].map((app, index) => (
                <div
                  key={`top-${app.id}-${index}`}
                  className="flex-shrink-0 w-14 h-14 md:w-20 md:h-20 bg-gray-900 rounded-lg flex items-center justify-center p-4"
                >
                  <img
                    src={app.icon}
                    alt={app.name}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "https://picsum.photos/200/300";
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* App Icons - Bottom Row with right to left animation */}
          <div className="overflow-hidden mb-6 marquee-container">
            <div
              ref={secondRowRef}
              className="flex space-x-4 py-2 marquee-second"
              style={{ width: 'fit-content' }}
            >
              {/* Double the items for seamless looping but in reverse order */}
              {[...appsToRender.slice().reverse(), ...appsToRender.slice().reverse()].map((app, index) => (
                <div
                  key={`bottom-${app.id}-${index}`}
                  className="flex-shrink-0 w-14 h-14 md:w-20 md:h-20 bg-gray-900 rounded-lg flex items-center justify-center p-4"
                >
                  <img
                    src={app.icon}
                    alt={app.name}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "https://picsum.photos/seed/picsum/200/300";
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Button - Mobile Position */}
          <div className="flex md:hidden justify-center mt-8">
            <Button
              variant="solid"
              color="primary"
              size="lg"
              onClick={() => {
                navigate("/integrations");
                window.scrollTo(0, 0);
              }}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
            >
              {buttonText}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HSIntegrations;